import { create } from 'zustand';

const useMapDataStore = create( ( set ) => ( {
  // State
  svgData: null,
  levelsData: [],
  mapFilterData: [],
  residenceStatusOptions: [],

  // Actions
  setSvgData: ( svgData ) => set( { svgData } ),
  setLevelsData: ( levelsData ) => set( { levelsData } ),
  setMapFilterData: ( mapFilterData ) => set( { mapFilterData } ),
  setResidenceStatusOptions: ( residenceStatusOptions ) => set( { residenceStatusOptions } ),

  // Bulk update from API response
  setMapData: ( data ) => set( {
    svgData: data.svgData,
    levelsData: data.mapLevels || [],
    mapFilterData: data.filterData || [],
    residenceStatusOptions: data.residenceStatusOptions || [],
  } ),

  /**
   * Update only occupancy/status data without replacing the full map structure.
   *
   * Takes the response from /map-occupancy endpoint which contains:
   * - occupancy: { residenceUuid: { name, uuid, weight, abbreviation, consider_unoccupied } }
   * - residenceStatusOptions: updated status options list
   *
   * Merges status data into existing svgData units and filterData entries
   * by matching on uuid, without touching SVG paths, levels, or other structure.
   */
  setOccupancyData: ( occupancyResponse ) => set( ( state ) => {
    const { occupancy, residenceStatusOptions: newStatusOptions } = occupancyResponse;

    if ( !state.svgData || !occupancy ) {
      return {
        residenceStatusOptions: newStatusOptions || state.residenceStatusOptions,
      };
    }

    // Deep-clone svgData levels to update units with new status.
    const updatedLevels = {};
    const updatedFilterData = [];

    Object.entries( state.svgData.levels || {} ).forEach( ( [ levelKey, level ] ) => {
      const updatedUnits = {};

      Object.entries( level.units || {} ).forEach( ( [ unitKey, unit ] ) => {
        const residenceUuid = unit?.info?.uuid;
        const newStatus = residenceUuid ? occupancy[residenceUuid] : null;

        if ( newStatus ) {
          // Create new unit with updated status.
          const updatedUnit = {
            ...unit,
            info: {
              ...unit.info,
              residence_status: newStatus,
            },
          };
          updatedUnits[unitKey] = updatedUnit;
          // Also update the filterData entry.
          updatedFilterData.push( {
            ...updatedUnit.info,
          } );
        } else {
          updatedUnits[unitKey] = unit;
          if ( unit?.info ) {
            updatedFilterData.push( { ...unit.info } );
          }
        }
      } );

      updatedLevels[levelKey] = {
        ...level,
        units: updatedUnits,
      };
    } );

    return {
      svgData: {
        ...state.svgData,
        levels: updatedLevels,
      },
      mapFilterData: updatedFilterData,
      residenceStatusOptions: newStatusOptions || state.residenceStatusOptions,
    };
  } ),
} ) );

export default useMapDataStore;
